import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Portal, Tooltip, useDisclosure } from '@chakra-ui/react';
import { snakeCase } from 'lodash';
import PropTypes from 'prop-types';

import { selectCompanyId } from '../../redux/modules/company';

const IconButtonMenu = ({
  id,
  menuOptions,
  iconElement,
  ariaLabel,
  blueprint,
  menuListWidth,
  variant = 'textGrey',
  width = '40px',
  size = 'md',
  isLoading = false,
  dusk = undefined,
  cypressId = undefined,
  metadata = {},
  ...props
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const companyId = useSelector(selectCompanyId);

  return (
    <Menu closeOnBlur isOpen={isOpen} ml={0} onClose={onClose} onOpen={onOpen}>
      <MenuButton
        _hover={{ cursor: isLoading ? 'progress' : 'pointer', color: '#AEC5D8' }}
        aria-label={ariaLabel}
        as={IconButton}
        data-cy={cypressId}
        dusk={dusk}
        icon={iconElement}
        id={id}
        isDisabled={isLoading}
        p={0}
        size={size}
        variant={variant}
        width={width}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          isOpen ? onClose() : onOpen();
        }}
        {...props}
      />
      <Portal w={menuListWidth}>
        <MenuList w={menuListWidth} zIndex="popover">
          {menuOptions.map(option => (
            <Tooltip hasArrow key={option.label} label={option.tooltip ?? null} zIndex="popover">
              <Box>
                <MenuItem
                  data-analytics-id={option?.dataAnalyticsId}
                  data-company-id={companyId}
                  data-cy={option.cypressId ?? `${cypressId}-${snakeCase(option.label)}`}
                  dusk={dusk ? `${dusk}-${option.label}` : `${option.label}-${option?.dataAnalyticsId}`}
                  icon={<Icon as={option?.icon} boxSize={option?.iconSize ?? 'inherit'} fontSize="sm" m={0} />}
                  isDisabled={option.isDisabled}
                  value={id}
                  onClick={e => option.handler({ e, dispatch, blueprint, history, ...metadata })}
                >
                  {option.label}
                </MenuItem>
              </Box>
            </Tooltip>
          ))}
        </MenuList>
      </Portal>
    </Menu>
  );
};

IconButtonMenu.propTypes = {
  /** any blueprint type * */
  blueprint: PropTypes.shape({}),
  /** Array of objects to be mapped over to create menu items */
  menuOptions: PropTypes.arrayOf(PropTypes.shape({
    /** Optional, will show on left of text */
    icon: PropTypes.func,
    /** Text for menu item */
    label: PropTypes.string,
    /** Event handler for menu item */
    handler: PropTypes.func,
    /** Optional, data attribute for tracking analytics */
    dataAnalyticsId: PropTypes.string,
    /** Optional, tooltip to display on hover */
    tooltip: PropTypes.string,
    /** Optional, disables the menu item from being selected */
    isDisabled: PropTypes.bool
  })),
  /** Icon for button. Should include boxSize and be entire <Icon as={icon} /> element */
  iconElement: PropTypes.element,
  /** Aria label for popup menu */
  ariaLabel: PropTypes.string,
  /** Dusk id for browser tests */
  dusk: PropTypes.string,
  /** Cypress id for e2e tests */
  cypressId: PropTypes.string,
  /** Width will default to 120px or auto, whichever is greater. Specifiy if you'd like a different width */
  menuListWidth: PropTypes.string,
  /** Width for the button */
  width: PropTypes.string,
  /** Size for the button if other than md */
  size: PropTypes.string,
  /** Variant for the trigger button */
  variant: PropTypes.string,
  /** ID of the menu and menu item, specified when menus are generated dynamically */
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Disables the menu button from triggering open the menu while true */
  isLoading: PropTypes.bool,
  /** Any data you want passed to the handler's callback */
  metadata: PropTypes.shape({})
};

export default IconButtonMenu;
